/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import type { ReactElement, ReactNode } from 'react';
import {
  getThemeColor,
  onebriefRemarkPlugins,
  mostReadable,
  inlineAllowedElements,
} from '../../utils';
import Icon from '../Icon';
import type { IconName } from '../Icon';
import Markdown from 'react-markdown';

type Props = {
  /** What do you want the banner to say */
  message?: ReactNode | string;
  /** What type of banner is this, pass in the color name, warning/danger/success come with a preset icon */
  color?:
    | 'primary'
    | 'secondary'
    | 'warning'
    | 'danger'
    | 'success'
    | 'black'
    | 'white'
    | 'border'
    | 'approved'
    | 'important'
    | 'unclear'
    | 'challenged'
    | 'info'
    | 'dark'
    | 'synced'
    | 'help'
    | string;
  className?: string;
  /** Make it a full width banner with no rounded corners */
  fullWidth?: boolean;
  /** Is there an action a user can perform, like undo? Pass in a button or 2 */
  action?: ReactNode;
  /** Horizontally align the content */
  justify?: 'left' | 'center' | 'right' | 'space-between';
  /** Pass in an icon or change the preset one */
  icon?: IconName;
  /** Make the banner more compact */
  compact?: boolean;
  /** Test id */
  testId?: string;
};

function ODSBanner({
  message,
  icon,
  color = 'primary',
  className,
  fullWidth,
  action,
  justify = 'center',
  compact,
  testId,
}: Props): ReactElement {
  let bannerIcon = icon;
  const bannerColor = getThemeColor(color) ?? color;
  if (!icon) {
    switch (color) {
      case 'warning':
        bannerIcon = 'triangle-exclamation';
        break;
      case 'danger':
        bannerIcon = 'hexagon-exclamation';
        break;
      case 'success':
        bannerIcon = 'check-circle';
        break;
      case 'info':
        bannerIcon = 'circle-info';
        break;
      case 'help':
        bannerIcon = 'circle-question';
        break;
      default:
    }
  }

  let text = 'text-start';
  let align = 'justify-content-between';

  if (!action) {
    switch (justify) {
      case 'left':
        align = 'justify-content-start';
        text = 'text-start';
        break;
      case 'right':
        align = 'justify-content-end';
        text = 'text-end';
        break;
      case 'center':
        align = 'justify-content-center';
        text = 'text-center';
        break;
      default:
    }
  }

  return (
    <div
      data-testid={testId}
      className={cx('d-flex align-items-center user-select-none gap-4', className, text, align, {
        'rounded mx-2 shadow-sm': !fullWidth,
      })}
      css={(theme) => ({
        zIndex: theme.zIndex.contentOverlay,
        background: bannerColor,
        color: mostReadable(bannerColor).toString(),
        ...theme.spacing.ps(3),
        ...theme.spacing.pe(2),
        ...theme.spacing.py(2),
        '@media print': {
          display: 'none !important',
        },
        a: {
          color: mostReadable(bannerColor).toString(),
          textDecoration: 'underline',
        },
      })}
    >
      <span className={cx(text, { 'w-100': fullWidth, 'pe-2': !action, 'py-1': !compact })}>
        {!!bannerIcon && <Icon name={bannerIcon} className="me-2" />}
        {typeof message !== 'string' ? (
          message
        ) : (
          <Markdown
            remarkPlugins={onebriefRemarkPlugins}
            allowedElements={inlineAllowedElements}
            unwrapDisallowed
          >
            {message}
          </Markdown>
        )}
      </span>
      {!!action && <div className="flex-shrink-0 d-flex align-self-center gap-2">{action}</div>}
    </div>
  );
}

export default ODSBanner;
